import { ReactNode, useRef } from 'react';
import { useDrop } from 'react-dnd';

import { AnnotationDragDescriptor, ANNOTATIONS_MARKER } from './AnnotationUtils';
import styles from './DragStyles.css';

type MarkerDropTargetProps = {
  children: ReactNode;
  onMove: (move: AnnotationDragDescriptor) => void;
};

export default function MarkerDropTarget({ children, onMove }: MarkerDropTargetProps) {
  const refDiv = useRef<HTMLDivElement>(null);

  const [_, refDropzone] = useDrop<AnnotationDragDescriptor>(
    () => ({
      accept: ANNOTATIONS_MARKER,
      drop: (item, monitor) => {
        if (refDiv.current && !!onMove) {
          // Get dragged marker item
          const descriptor = monitor.getItem();
          // Position and dimension of dragging area (container)
          const {
            x: containerX,
            y: containerY,
            height: containerHeight,
            width: containerWidth,
          } = refDiv.current.getBoundingClientRect();
          // Position of dragged marker
          const { x: markerX, y: markerY } = monitor.getSourceClientOffset();
          // Calc relative marker position inside dragging area
          const relMarkerX = markerX - containerX;
          const relMarkerY = markerY - containerY;
          // Calc percentage marker position inside dragging area
          const perMarkerX = (relMarkerX / containerWidth) * 100;
          const perMarkerY = (relMarkerY / containerHeight) * 100;
          // Constrain to 0 -> 100
          const conMarkerX = Math.max(0, Math.min(perMarkerX, 100));
          const conMarkerY = Math.max(0, Math.min(perMarkerY, 100));
          // Round to max 2 decimals
          const roundPerMarkerX = Math.round((conMarkerX + Number.EPSILON) * 100) / 100;
          const roundPerMarkerY = Math.round((conMarkerY + Number.EPSILON) * 100) / 100;

          onMove({
            id: descriptor.id,
            left: roundPerMarkerX,
            top: roundPerMarkerY,
          });
        }
      },
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [onMove],
  );

  // Wrap ref
  refDropzone(refDiv);

  return (
    <div ref={refDiv} className={styles.dropTargetContainer}>
      <div className={styles.dropTarget}>{children}</div>
    </div>
  );
}
